/**
 * Depreciation Service
 * Handles automated depreciation posting to general ledger
 */

import { supabase } from '@/lib/supabase'

export interface DepreciationEntry {
  asset_id: string
  asset_code: string
  asset_name: string
  depreciation_date: string
  fiscal_year: number
  fiscal_period: number
  depreciation_amount: number
  accumulated_depreciation_before: number
  accumulated_depreciation_after: number
  book_value_after: number
  asset_account_id: string
  depreciation_expense_account_id: string
  accumulated_depreciation_account_id: string
}

export interface DepreciationPostingResult {
  success: boolean
  journal_entry_id?: string
  assets_processed: number
  total_depreciation: number
  errors: Array<{
    asset_id: string
    asset_name: string
    error: string
  }>
}

export class DepreciationService {
  /**
   * Calculate monthly depreciation for an asset using straight-line method
   */
  static calculateStraightLineDepreciation(
    purchaseCost: number,
    salvageValue: number,
    usefulLifeYears: number
  ): number {
    if (usefulLifeYears <= 0) return 0
    const depreciableAmount = purchaseCost - salvageValue
    const monthlyDepreciation = depreciableAmount / usefulLifeYears / 12
    return Math.round(monthlyDepreciation * 100) / 100 // Round to 2 decimal places
  }

  /**
   * Calculate declining balance depreciation
   */
  static calculateDecliningBalanceDepreciation(
    currentBookValue: number,
    usefulLifeYears: number,
    rate: number = 2 // Double-declining balance by default
  ): number {
    if (usefulLifeYears <= 0) return 0
    const annualRate = rate / usefulLifeYears
    const monthlyRate = annualRate / 12
    const monthlyDepreciation = currentBookValue * monthlyRate
    return Math.round(monthlyDepreciation * 100) / 100
  }

  /**
   * Get all assets that need depreciation for a given period
   */
  static async getAssetsForDepreciation(
    churchId: string,
    periodDate: Date
  ): Promise<DepreciationEntry[]> {
    const fiscalYear = periodDate.getFullYear()
    const fiscalPeriod = periodDate.getMonth() + 1

    // Get all active assets with depreciation settings
    const { data: assets, error } = await supabase
      .from('assets')
      .select(`
        id,
        asset_code,
        asset_name,
        purchase_cost,
        salvage_value,
        accumulated_depreciation,
        book_value,
        depreciation_method,
        useful_life_years,
        depreciation_start_date,
        asset_account_id,
        depreciation_expense_account_id,
        accumulated_depreciation_account_id,
        purchase_date
      `)
      .eq('church_id', churchId)
      .eq('status', 'active')
      .neq('depreciation_method', 'none')
      .not('depreciation_expense_account_id', 'is', null)
      .not('accumulated_depreciation_account_id', 'is', null)

    if (error) throw error
    if (!assets || assets.length === 0) return []

    const depreciationEntries: DepreciationEntry[] = []

    for (const asset of assets) {
      // Check if asset should be deprecated this period
      const depStartDate = new Date(asset.depreciation_start_date || asset.purchase_date)

      if (periodDate < depStartDate) {
        continue // Asset depreciation hasn't started yet
      }

      // Check if already posted for this period
      const { data: existing } = await supabase
        .from('asset_depreciation_history')
        .select('id')
        .eq('asset_id', asset.id)
        .eq('fiscal_year', fiscalYear)
        .eq('fiscal_period', fiscalPeriod)
        .not('journal_entry_id', 'is', null)
        .single()

      if (existing) {
        continue // Already posted for this period
      }

      // Calculate depreciation
      let depreciationAmount = 0

      if (asset.depreciation_method === 'straight_line') {
        depreciationAmount = this.calculateStraightLineDepreciation(
          asset.purchase_cost,
          asset.salvage_value,
          asset.useful_life_years
        )
      } else if (asset.depreciation_method === 'declining_balance') {
        depreciationAmount = this.calculateDecliningBalanceDepreciation(
          asset.book_value,
          asset.useful_life_years
        )
      }

      // Ensure we don't depreciate below salvage value
      const maxDepreciation = asset.book_value - asset.salvage_value
      if (depreciationAmount > maxDepreciation) {
        depreciationAmount = maxDepreciation
      }

      // Skip if depreciation amount is zero or negative
      if (depreciationAmount <= 0) {
        continue
      }

      const accumulatedBefore = asset.accumulated_depreciation
      const accumulatedAfter = accumulatedBefore + depreciationAmount
      const bookValueAfter = asset.purchase_cost - accumulatedAfter

      depreciationEntries.push({
        asset_id: asset.id,
        asset_code: asset.asset_code,
        asset_name: asset.asset_name,
        depreciation_date: periodDate.toISOString().split('T')[0],
        fiscal_year: fiscalYear,
        fiscal_period: fiscalPeriod,
        depreciation_amount: depreciationAmount,
        accumulated_depreciation_before: accumulatedBefore,
        accumulated_depreciation_after: accumulatedAfter,
        book_value_after: bookValueAfter,
        asset_account_id: asset.asset_account_id,
        depreciation_expense_account_id: asset.depreciation_expense_account_id!,
        accumulated_depreciation_account_id: asset.accumulated_depreciation_account_id!,
      })
    }

    return depreciationEntries
  }

  /**
   * Post depreciation entries to general ledger
   */
  static async postDepreciationToLedger(
    churchId: string,
    periodDate: Date,
    userId: string,
    entries?: DepreciationEntry[]
  ): Promise<DepreciationPostingResult> {
    const result: DepreciationPostingResult = {
      success: false,
      assets_processed: 0,
      total_depreciation: 0,
      errors: [],
    }

    try {
      // Get entries if not provided
      if (!entries) {
        entries = await this.getAssetsForDepreciation(churchId, periodDate)
      }

      if (entries.length === 0) {
        result.success = true
        return result
      }

      const fiscalYear = periodDate.getFullYear()
      const fiscalPeriod = periodDate.getMonth() + 1
      const totalDepreciation = entries.reduce((sum, e) => sum + e.depreciation_amount, 0)

      // Generate entry number
      const entryNumber = `JE-${fiscalYear}${fiscalPeriod.toString().padStart(2, '0')}-DEP-${Date.now()}`

      // Create journal entry header
      const { data: journalEntry, error: jeError } = await supabase
        .from('general_ledger')
        .insert({
          church_id: churchId,
          entry_number: entryNumber,
          entry_date: periodDate.toISOString().split('T')[0],
          posting_date: new Date().toISOString().split('T')[0],
          source_type: 'depreciation',
          status: 'posted',
          fiscal_year: fiscalYear,
          fiscal_period: fiscalPeriod,
          description: `Monthly depreciation for ${periodDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`,
          reference: `DEP-${fiscalYear}-${fiscalPeriod}`,
          total_debit: totalDepreciation,
          total_credit: totalDepreciation,
          requires_approval: false,
          created_by: userId,
          posted_by: userId,
        })
        .select()
        .single()

      if (jeError) {
        throw new Error(`Failed to create journal entry: ${jeError.message}`)
      }

      // Group entries by account for journal lines
      const debitLines = new Map<string, number>()
      const creditLines = new Map<string, number>()

      for (const entry of entries) {
        // Accumulate debits by expense account
        const currentDebit = debitLines.get(entry.depreciation_expense_account_id) || 0
        debitLines.set(entry.depreciation_expense_account_id, currentDebit + entry.depreciation_amount)

        // Accumulate credits by accumulated depreciation account
        const currentCredit = creditLines.get(entry.accumulated_depreciation_account_id) || 0
        creditLines.set(entry.accumulated_depreciation_account_id, currentCredit + entry.depreciation_amount)
      }

      // Create journal lines
      const journalLines: any[] = []
      let lineNumber = 1

      // Debit lines (Depreciation Expense)
      for (const [accountId, amount] of debitLines.entries()) {
        journalLines.push({
          journal_entry_id: journalEntry.id,
          church_id: churchId,
          line_number: lineNumber++,
          account_id: accountId,
          debit_amount: amount,
          credit_amount: 0,
          description: `Depreciation expense for ${periodDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`,
        })
      }

      // Credit lines (Accumulated Depreciation)
      for (const [accountId, amount] of creditLines.entries()) {
        journalLines.push({
          journal_entry_id: journalEntry.id,
          church_id: churchId,
          line_number: lineNumber++,
          account_id: accountId,
          debit_amount: 0,
          credit_amount: amount,
          description: `Accumulated depreciation for ${periodDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`,
        })
      }

      const { error: linesError } = await supabase
        .from('general_ledger_lines')
        .insert(journalLines)

      if (linesError) {
        throw new Error(`Failed to create journal lines: ${linesError.message}`)
      }

      // Create depreciation history entries and update assets
      for (const entry of entries) {
        try {
          // Insert depreciation history
          const { error: historyError } = await supabase
            .from('asset_depreciation_history')
            .insert({
              asset_id: entry.asset_id,
              church_id: churchId,
              depreciation_date: entry.depreciation_date,
              fiscal_year: entry.fiscal_year,
              fiscal_period: entry.fiscal_period,
              depreciation_amount: entry.depreciation_amount,
              accumulated_depreciation_before: entry.accumulated_depreciation_before,
              accumulated_depreciation_after: entry.accumulated_depreciation_after,
              book_value_after: entry.book_value_after,
              journal_entry_id: journalEntry.id,
              created_by: userId,
            })

          if (historyError) {
            throw new Error(`History: ${historyError.message}`)
          }

          // Update asset
          const { error: assetError } = await supabase
            .from('assets')
            .update({
              accumulated_depreciation: entry.accumulated_depreciation_after,
              book_value: entry.book_value_after,
              last_depreciation_date: entry.depreciation_date,
              updated_at: new Date().toISOString(),
            })
            .eq('id', entry.asset_id)

          if (assetError) {
            throw new Error(`Asset update: ${assetError.message}`)
          }

          result.assets_processed++
        } catch (error) {
          result.errors.push({
            asset_id: entry.asset_id,
            asset_name: entry.asset_name,
            error: error instanceof Error ? error.message : 'Unknown error',
          })
        }
      }

      result.success = result.errors.length === 0
      result.journal_entry_id = journalEntry.id
      result.total_depreciation = totalDepreciation

      return result
    } catch (error) {
      throw new Error(
        `Depreciation posting failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  /**
   * Preview depreciation entries without posting
   */
  static async previewDepreciation(
    churchId: string,
    periodDate: Date
  ): Promise<DepreciationEntry[]> {
    return await this.getAssetsForDepreciation(churchId, periodDate)
  }

  /**
   * Get depreciation history for an asset
   */
  static async getAssetDepreciationHistory(assetId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('asset_depreciation_history')
      .select(`
        *,
        journal_entry:general_ledger(entry_number, entry_date, status)
      `)
      .eq('asset_id', assetId)
      .order('depreciation_date', { ascending: false })

    if (error) throw error
    return data || []
  }

  /**
   * Reverse depreciation entry (for corrections)
   */
  static async reverseDepreciation(
    journalEntryId: string,
    userId: string,
    reason: string
  ): Promise<void> {
    // Get original journal entry
    const { data: originalEntry, error: fetchError } = await supabase
      .from('general_ledger')
      .select('*')
      .eq('id', journalEntryId)
      .single()

    if (fetchError) throw fetchError

    // Create reversal entry
    const reversalNumber = `${originalEntry.entry_number}-REV`

    const { data: reversalEntry, error: reversalError } = await supabase
      .from('general_ledger')
      .insert({
        church_id: originalEntry.church_id,
        entry_number: reversalNumber,
        entry_date: new Date().toISOString().split('T')[0],
        posting_date: new Date().toISOString().split('T')[0],
        source_type: 'depreciation',
        status: 'posted',
        fiscal_year: originalEntry.fiscal_year,
        fiscal_period: originalEntry.fiscal_period,
        description: `Reversal: ${originalEntry.description}`,
        reference: `REV-${originalEntry.reference}`,
        total_debit: originalEntry.total_credit,
        total_credit: originalEntry.total_debit,
        is_reversal: true,
        reverses_entry_id: journalEntryId,
        reversal_reason: reason,
        created_by: userId,
        posted_by: userId,
      })
      .select()
      .single()

    if (reversalError) throw reversalError

    // Get original lines and create reversed lines
    const { data: originalLines, error: linesError } = await supabase
      .from('general_ledger_lines')
      .select('*')
      .eq('journal_entry_id', journalEntryId)

    if (linesError) throw linesError

    const reversalLines = originalLines.map((line, index) => ({
      journal_entry_id: reversalEntry.id,
      church_id: line.church_id,
      line_number: index + 1,
      account_id: line.account_id,
      debit_amount: line.credit_amount, // Swap debit and credit
      credit_amount: line.debit_amount,
      description: `Reversal: ${line.description}`,
    }))

    const { error: reversalLinesError } = await supabase
      .from('general_ledger_lines')
      .insert(reversalLines)

    if (reversalLinesError) throw reversalLinesError

    // Mark original entry as reversed
    await supabase
      .from('general_ledger')
      .update({ reversed_by_entry_id: reversalEntry.id })
      .eq('id', journalEntryId)

    // Update depreciation history to remove journal entry reference
    await supabase
      .from('asset_depreciation_history')
      .update({ journal_entry_id: null })
      .eq('journal_entry_id', journalEntryId)
  }
}

export default DepreciationService
